import { cloneDeep, get, isObject, isPlainObject, set, unset } from 'lodash';
import { EntityName } from '../../auth/Security';
import {
    ADDITIONAL_ENTITY_FIELDS_FOR_DEEP_LINK_UI_STATE,
    DEEP_LINK_ALLOWED_QUERY_PARAMS,
    DEEP_LINK_RESOLVER_ROUTE_PATH,
    DEEP_LINK_UI_QUERY_PARAM_KEY,
    ENABLE_DEEP_LINK_DEBUG,
    LINKED_ENTITY_NODE_KEY_TO_SIDE_PANEL_ID_MAP,
    LINKED_ENTITY_NODE_KEYS
} from '../constants';
import { DEEP_LINK_ROUTE_MAPPINGS } from '../deepLinkRouteMappings';
import type {
    DeepLinkUIState,
    DeepLinkUIStateLinkedEntityNodeObjectValue,
    DeepLinkUIStateMainEntityNodeObjectValue
} from '../types';

const resolveRoutePatternFromMapping = (
    mapping: (typeof DEEP_LINK_ROUTE_MAPPINGS)[string] | undefined,
    entityData: Record<string, unknown>
): string | undefined => {
    if (!mapping) {
        return undefined;
    }

    const matchedRule = mapping.routePatternRules?.find((rule) => {
        if (!rule.when) {
            return true;
        }

        const currentValue = get(entityData, rule.when.path);

        if (currentValue === undefined || isObject(currentValue)) {
            return undefined;
        }

        const expectedValue = rule.when.equals;

        if (typeof expectedValue === 'string') {
            return typeof currentValue === 'string' && currentValue.toLowerCase() === expectedValue.toLowerCase();
        }

        return currentValue === expectedValue;
    });

    return matchedRule?.routePattern ?? mapping.routePattern;
};

export const getValueAtPath = <T = Record<string, unknown>>(
    value: Record<string, unknown> | undefined,
    path?: string
): T | undefined => {
    const currentValue = path ? get(value, path) : value;

    if (currentValue === undefined) {
        return undefined;
    }

    return cloneDeep(currentValue) as T;
};

export const setValueAtPath = (
    value: Record<string, unknown> | undefined,
    path: string,
    nextValue: unknown,
    merge = false
): Record<string, unknown> => {
    const safelyInitializedValue = cloneDeep(value ?? {}) as Record<string, unknown>;

    const currentValueAtPath = get(safelyInitializedValue, path) as Record<string, unknown> | undefined;
    const nextValueAtPath =
        merge && isPlainObject(currentValueAtPath) && isPlainObject(nextValue)
            ? {
                  ...currentValueAtPath,
                  ...cloneDeep(nextValue as Record<string, unknown>)
              }
            : cloneDeep(nextValue);

    set(safelyInitializedValue, path, nextValueAtPath);
    return safelyInitializedValue;
};

export const removeValueAtPath = (
    value: Record<string, unknown> | undefined,
    path?: string
): Record<string, unknown> | undefined => {
    if (!value) {
        return undefined;
    }

    if (!path) {
        return undefined;
    }

    const nextValue = cloneDeep(value);
    unset(nextValue, path);

    /**
     * @todo
     * Revisit this logic.
     * The path being unset could be deep within the `value` object.
     * But the below check only checks if the top level of the resulting object is empty
     * which might not be the case and we might end up returning an empty object instead of `undefined`
     * even when there is no meaningful value left in the object after unsetting the path.
     */
    // if (Object.keys(nextValue).length === 0) {
    //     return undefined;
    // }

    return nextValue;
};

export const hasAnyState = (store: DeepLinkUIState): boolean => {
    return Object.entries(store).some(([, value]) => value !== undefined);
};

const getPathDepth = (path: string): number => {
    return path.split('.').length;
};

export const getDeepestDeepLinkUIStatePath = (obj: Record<string, unknown>): string => {
    let deepestMainEntityIdPath = '';
    let deepestLinkedEntityIdPath = '';

    /**
     * Recursively traverse the object tree to find entity ID paths.
     * Maintains two separate tracking paths:
     * - Linked entity ID paths (e.g., 'production.note.id', 'person.attachment.id')
     * - Main entity ID paths (e.g., 'production.id', 'opportunity.resource.id')
     *
     * Linked entity paths take priority when both exist.
     */
    const visit = (value: unknown, pathSegments: string[], isInsideLinkedEntity: boolean): void => {
        if (!isPlainObject(value)) {
            return;
        }

        // Check if current object has an 'id' field (indicating it's an entity)
        const entityId = get(value, 'id');
        if (typeof entityId === 'string') {
            const idPath = [...pathSegments, 'id'].join('.');

            if (isInsideLinkedEntity) {
                // Track paths inside linked entity nodes (note, attachment, etc.)
                if (getPathDepth(idPath) > getPathDepth(deepestLinkedEntityIdPath)) {
                    deepestLinkedEntityIdPath = idPath;
                }
            } else {
                // Track main entity paths (production, resource, opportunity, person, etc.)
                if (getPathDepth(idPath) > getPathDepth(deepestMainEntityIdPath)) {
                    deepestMainEntityIdPath = idPath;
                }
            }
        }

        // Traverse nested objects, tracking whether we're inside a linked entity branch
        Object.entries(value as Record<string, unknown>).forEach(([key, nestedValue]) => {
            if (!isPlainObject(nestedValue)) {
                return;
            }

            const isLinkedEntityNode = LINKED_ENTITY_NODE_KEYS.includes(key);
            visit(nestedValue, [...pathSegments, key], isInsideLinkedEntity || isLinkedEntityNode);
        });
    };

    visit(obj, [], false);

    // Prioritize linked entity ID paths; fall back to main entity ID paths
    if (deepestLinkedEntityIdPath) {
        return deepestLinkedEntityIdPath;
    }

    return deepestMainEntityIdPath;
};

export const parseUIStateFromSearch = (): DeepLinkUIState => {
    try {
        if (typeof window === 'undefined') {
            throw new Error('parseUIStateFromSearch can only be called in a browser environment');
        }

        const currentURL = new URL(window.location.href);
        const rawValue = currentURL.searchParams.get(DEEP_LINK_UI_QUERY_PARAM_KEY);

        if (!rawValue) {
            return {};
        }

        const decodedRawValue = decodeURIComponent(rawValue);
        const parsedValue = JSON.parse(decodedRawValue) as unknown;

        if (!isPlainObject(parsedValue)) {
            return {};
        }

        return parsedValue as DeepLinkUIState;
    } catch (e) {
        console.warn('Failed to parse deep link UI state from URL search params:', e);
        return {};
    }
};

export const getLinkedEntityNodeKeyForMainEntityUIState = (
    mainEntityUIState: Record<string, unknown>
): string | undefined => {
    let linkedEntityNodeKeys = [];

    if (!isPlainObject(mainEntityUIState)) {
        console.warn('Main entity UI state is not a plain object:', mainEntityUIState);
        return undefined;
    }

    Object.keys(mainEntityUIState)
        .filter((key) => LINKED_ENTITY_NODE_KEYS.includes(key))
        .forEach((linkedEntityNode) => linkedEntityNodeKeys.push(linkedEntityNode));

    if (linkedEntityNodeKeys.length === 0) {
        return undefined;
    }

    if (linkedEntityNodeKeys.length > 1) {
        console.warn(
            `Multiple linked entity node keys [${linkedEntityNodeKeys.join(', ')}] found in deep link UI state: `,
            mainEntityUIState
        );
        return undefined;
    }

    const linkedEntityNodeKey = linkedEntityNodeKeys[0];
    return linkedEntityNodeKey;
};

export const getLinkedEntitySidePanelIdForMainEntityUIState = (
    mainEntityUIState: Record<string, unknown> | undefined
): string | undefined => {
    if (!isPlainObject(mainEntityUIState)) {
        return undefined;
    }

    const linkedEntityNodeKey = getLinkedEntityNodeKeyForMainEntityUIState(mainEntityUIState);
    if (!linkedEntityNodeKey) {
        return undefined;
    }

    const mappedPanelId = LINKED_ENTITY_NODE_KEY_TO_SIDE_PANEL_ID_MAP[linkedEntityNodeKey];
    if (!mappedPanelId) {
        console.warn(`No mapped side panel id found for linked entity node key "${linkedEntityNodeKey}".`);
        return undefined;
    }

    return mappedPanelId;
};

export const getTabKeyForMainEntityUIState = (
    mainEntityUIState: Record<string, unknown> | undefined
): string | undefined => {
    if (!isPlainObject(mainEntityUIState)) {
        return undefined;
    }

    const tabKeyCandidates = Object.entries(mainEntityUIState)
        .filter(([key, value]) => {
            if (LINKED_ENTITY_NODE_KEYS.includes(key)) {
                return false;
            }

            return isPlainObject(value);
        })
        .map(([key]) => key);

    if (tabKeyCandidates.length === 0) {
        return undefined;
    }

    if (tabKeyCandidates.length > 1) {
        console.warn(
            `Multiple child entity node keys [${tabKeyCandidates.join(', ')}] found in deep link UI state for tab resolution:`,
            mainEntityUIState
        );
        return undefined;
    }

    return tabKeyCandidates[0];
};

export const isValidLinkedEntityNodeUIStatePath = (path: string): boolean => {
    const pathSegments = path.split('.');
    const lastSegment = pathSegments[pathSegments.length - 1];
    return LINKED_ENTITY_NODE_KEYS.includes(lastSegment);
};

export const isValidLinkedEntityNodeObjectValue = (
    value: unknown
): value is DeepLinkUIStateLinkedEntityNodeObjectValue => {
    if (
        isPlainObject(value) &&
        Object.keys(value).length === 1 &&
        typeof (value as DeepLinkUIStateLinkedEntityNodeObjectValue).id === 'string'
    ) {
        return true;
    }

    return false;
};

export const getUIStatePathSegments = (
    path: string
): { mainEntityPath: string; linkedEntityPath: string | undefined } => {
    const segments = path.split('.');
    const linkedEntityNodeIndex = segments.findIndex((segment) => LINKED_ENTITY_NODE_KEYS.includes(segment));

    if (linkedEntityNodeIndex === -1) {
        const endsWithId = segments[segments.length - 1] === 'id';
        return {
            mainEntityPath: endsWithId ? segments.slice(0, -1).join('.') : path,
            linkedEntityPath: undefined
        };
    }

    return {
        mainEntityPath: segments.slice(0, linkedEntityNodeIndex).join('.'),
        linkedEntityPath: segments.slice(linkedEntityNodeIndex).join('.')
    };
};

export const getRoutePathFromDeepLinkUIState = (uiState: DeepLinkUIState): string | undefined => {
    if (!isPlainObject(uiState)) {
        console.warn('Invalid UI state for deep linking:', uiState);
        return;
    }

    const deepestPath = getDeepestDeepLinkUIStatePath(uiState);
    if (!deepestPath) {
        console.warn('No traversable path found in UI state for deep linking:', uiState);
        return;
    }

    const { mainEntityPath } = getUIStatePathSegments(deepestPath);
    if (!mainEntityPath) {
        console.warn('Could not determine main entity path from deepest path:', deepestPath, uiState);
        return;
    }

    const deepLinkRouteMapping = DEEP_LINK_ROUTE_MAPPINGS[mainEntityPath];
    if (!deepLinkRouteMapping) {
        console.warn('No deep link route mapping found for main entity path:', mainEntityPath, uiState);
        return;
    }

    const mainEntityRoutePattern = resolveRoutePatternFromMapping(deepLinkRouteMapping, uiState);
    if (!mainEntityRoutePattern) {
        console.warn(
            'No route pattern found for main entity path in deep link route mapping:',
            mainEntityPath,
            uiState
        );
        return;
    }

    let routePath = mainEntityRoutePattern;

    const mainEntityRoutePlaceholders =
        routePath.match(/\{([^{}]+)\}/g)?.map((placeholder) => placeholder.replace(/[{}]/g, '')) ?? [];

    if (mainEntityRoutePlaceholders.length) {
        for (const routePlaceholder of mainEntityRoutePlaceholders) {
            const routePlaceholderPath = routePlaceholder;

            const routePlaceholderUIStateValue = get(uiState, routePlaceholderPath) as string | undefined;

            if (typeof routePlaceholderUIStateValue !== 'string') {
                console.warn(
                    `No string value found in UI state for the '${routePlaceholderPath}' placeholder in the route pattern ${mainEntityRoutePattern}`,
                    uiState
                );
                return;
            }

            routePath = routePath.replace(`{${routePlaceholder}}`, routePlaceholderUIStateValue);
        }
    }

    return routePath;
};

const carveUIStateSliceByPath = (sourceValue: Record<string, unknown>, path: string): DeepLinkUIState | undefined => {
    const pathSegments = path.split('.').filter(Boolean);
    const linkedEntityNodeIndex = pathSegments.findIndex((segment) => LINKED_ENTITY_NODE_KEYS.includes(segment));
    const deepestNonLinkedEntityNodeIndex =
        linkedEntityNodeIndex === -1 ? pathSegments.length - 1 : linkedEntityNodeIndex - 1;
    const rootMainEntityNodeKeys = new Set(Object.keys(DEEP_LINK_ROUTE_MAPPINGS).map((key) => key.split('.')[0]));

    if (pathSegments.length === 0) {
        return undefined;
    }

    const carveRecursively = (
        currentValue: unknown,
        remainingSegments: string[],
        traversedSegmentCount: number
    ): unknown => {
        if (remainingSegments.length === 0) {
            return cloneDeep(currentValue);
        }

        if (!isPlainObject(currentValue)) {
            return undefined;
        }

        const [nextSegment, ...nextRemainingSegments] = remainingSegments;
        const nextNestedValue = get(currentValue, nextSegment);

        if (nextNestedValue === undefined) {
            return undefined;
        }

        const carvedNestedValue = carveRecursively(nextNestedValue, nextRemainingSegments, traversedSegmentCount + 1);

        if (carvedNestedValue === undefined) {
            return undefined;
        }

        const nextValue: Record<string, unknown> = {};
        const isRootNode = traversedSegmentCount === 0;
        const isDeepestNonLinkedEntityNode =
            deepestNonLinkedEntityNodeIndex >= 0 && traversedSegmentCount === deepestNonLinkedEntityNodeIndex + 1;

        /**
         * Keep scalar metadata (e.g. id/state flags) required to resolve route mappings and placeholders.
         * Avoid carrying stale or unrelated top-level entries and only keep linked-entity scalar flags
         * at the deepest non-linked node of the requested path.
         */
        Object.entries(currentValue).forEach(([key, value]) => {
            if (isPlainObject(value)) {
                return;
            }

            if (isRootNode && rootMainEntityNodeKeys.has(key)) {
                return;
            }

            if (isRootNode) {
                return;
            }

            if (LINKED_ENTITY_NODE_KEYS.includes(key) && !isDeepestNonLinkedEntityNode) {
                return;
            }

            nextValue[key] = cloneDeep(value);
        });

        nextValue[nextSegment] = carvedNestedValue;

        return nextValue;
    };

    const carvedState = carveRecursively(sourceValue, pathSegments, 0);

    if (!isPlainObject(carvedState)) {
        return undefined;
    }

    return carvedState as DeepLinkUIState;
};

export const buildDeepLinkURLFromState = (
    uiState: DeepLinkUIState,
    deepLinkRequestUIStatePath: string,
    applicationId: string,
    overrideStateSlice?: Record<string, unknown>
): string => {
    if (typeof window === 'undefined') {
        console.warn('buildDeepLinkURLFromState can only be called in a browser environment');
        return '';
    }

    const currentURL = new URL(window.location.href);
    const nextURL = new URL(currentURL.origin);

    nextURL.pathname = DEEP_LINK_RESOLVER_ROUTE_PATH.replace(':appId', encodeURIComponent(applicationId));

    const nextSearchParams = new URLSearchParams();

    currentURL.searchParams.forEach((value, key) => {
        if (!DEEP_LINK_ALLOWED_QUERY_PARAMS.includes(key)) {
            return;
        }

        nextSearchParams.set(key, value);
    });

    /**
     * If `overrideStateSlice` is passed then first override the value at the `deepLinkRequestUIStatePath`
     * in the `uiState` with the `overrideStateSlice` and then carve out the slice from the resulting state.
     */
    let uiStateToCarve = uiState;
    if (overrideStateSlice) {
        uiStateToCarve = setValueAtPath(uiState, deepLinkRequestUIStatePath, overrideStateSlice);
    }

    const deepLinkRequestUIState = carveUIStateSliceByPath(uiStateToCarve, deepLinkRequestUIStatePath);

    if (!deepLinkRequestUIState || !hasAnyState(deepLinkRequestUIState)) {
        console.warn('No deep link UI state found for path when building deep link URL:', deepLinkRequestUIStatePath);
    } else {
        const encodedUIState = encodeURIComponent(JSON.stringify(deepLinkRequestUIState));
        nextSearchParams.set(DEEP_LINK_UI_QUERY_PARAM_KEY, encodedUIState);
    }

    nextURL.search = nextSearchParams.toString();

    return nextURL.toString();
};

export function deepLinkLog(
    logFn: 'log' | 'info' | 'trace' | 'debug' | 'group' | 'groupEnd' = 'info',
    ...args: unknown[]
): void {
    if (ENABLE_DEEP_LINK_DEBUG) {
        console[logFn]('[DeepLink]', ...args);
    }
}

export const isValidMainEntityNodeObjectValue = (value: unknown): value is DeepLinkUIStateMainEntityNodeObjectValue => {
    if (isPlainObject(value) && typeof (value as DeepLinkUIStateMainEntityNodeObjectValue).id === 'string') {
        return true;
    }

    return false;
};

export const buildEntityDeepLinkUIStateObject = (
    entityName: EntityName,
    entityData: Record<string, unknown> | undefined
): DeepLinkUIStateMainEntityNodeObjectValue | undefined => {
    if (entityData === undefined) {
        return undefined;
    }

    if (!isPlainObject(entityData)) {
        console.warn('Invalid entity data provided to buildEntityDeepLinkUIStateObject:', entityData);
        return undefined;
    }

    const entityId =
        (entityData.id as string) ??
        (entityData.reference as { id: string })?.id ??
        (entityData.identity as { id: string })?.id;

    if (!entityId) {
        console.warn('No entity ID found in entity data provided to buildEntityDeepLinkUIStateObject:', entityData);
        return undefined;
    }

    const stateObject: DeepLinkUIStateMainEntityNodeObjectValue = {
        id: entityId
    };

    const additionalFields = ADDITIONAL_ENTITY_FIELDS_FOR_DEEP_LINK_UI_STATE[entityName];
    if (additionalFields?.length > 0) {
        additionalFields.forEach((field) => {
            const fieldValue = entityData[field];
            if (fieldValue !== undefined && !isObject(fieldValue)) {
                stateObject[field] = fieldValue;
            }
        });
    }

    return stateObject;
};
